import {EventOfferLineItemTypeValue, EventOfferLineSourceTypeValue, PayerTypeValue, PaymentMethodCategoryValue, PaymentMethodValue, PublicEventFormResponse, PublicRegistrationFormValues, PublicRegistrationSubmitPayload, isPayableRegistrationItem, requiresCompanyStylePayerFields} from "./types";
import {calculateNightCount, filterPublicRegistrationOptionsForParticipationFee, findRoomAvailability, formatDateOnly, getActivePublicRegistrationChoiceIds, getPaymentMethodsForCategory, normalizePublicRegistrationOptionQuestionType, serializeAnswerValue, trimValue} from "./publicRegistrationModel";

export const buildPublicRegistrationActivityAnswers = (
    values: PublicRegistrationFormValues,
    form: PublicEventFormResponse
): PublicRegistrationSubmitPayload["activityAnswers"] => {
    const selectedParticipationFee = form.participationFees
        ?.find(fee => fee.feePriceId === Number(values.selectedParticipationFeePriceId));
    const selectedYesNoOptionIds = new Set((values.selectedOptionIds ?? []).map(Number));
    const availableRegistrationOptions = filterPublicRegistrationOptionsForParticipationFee(
        form.registrationOptions,
        selectedParticipationFee,
        values.selectedOptionIds);
    const rawActivityAnswers = values.activityAnswers ?? {};

    return availableRegistrationOptions
        .reduce<PublicRegistrationSubmitPayload["activityAnswers"]>((answers, option) => {
            const questionType = normalizePublicRegistrationOptionQuestionType(option.questionType);
            const key = String(option.id);

            if (questionType === "YesNo") {
                if (selectedYesNoOptionIds.has(option.id)) {
                    answers.push({
                        eventOptionId: option.id,
                        isSelected: true,
                        selectedChoiceIds: []
                    });
                }

                return answers;
            }

            if (questionType === "SingleChoice") {
                const choiceId = Number(rawActivityAnswers[key]);
                if (getActivePublicRegistrationChoiceIds(option).has(choiceId)) {
                    answers.push({
                        eventOptionId: option.id,
                        isSelected: true,
                        selectedChoiceIds: [choiceId]
                    });
                }

                return answers;
            }

            if (questionType === "MultiChoice") {
                const allowedChoiceIds = getActivePublicRegistrationChoiceIds(option);
                const selectedChoiceIds = Array.isArray(rawActivityAnswers[key])
                    ? rawActivityAnswers[key]
                        .map(Number)
                        .filter((choiceId: number) => allowedChoiceIds.has(choiceId))
                    : [];

                if (selectedChoiceIds.length > 0) {
                    answers.push({
                        eventOptionId: option.id,
                        isSelected: true,
                        selectedChoiceIds: Array.from(new Set(selectedChoiceIds)).sort((left, right) => left - right)
                    });
                }

                return answers;
            }

            const textValue = trimValue(typeof rawActivityAnswers[key] === "string" ? rawActivityAnswers[key] : "");
            if (textValue) {
                answers.push({
                    eventOptionId: option.id,
                    isSelected: true,
                    textValue,
                    selectedChoiceIds: []
                });
            }

            return answers;
        }, []);
};

export const buildPublicRegistrationSubmitPayload = (
    values: PublicRegistrationFormValues,
    form: PublicEventFormResponse
): PublicRegistrationSubmitPayload => {
    const fields = form.template?.fields ?? [];
    const answers = fields.map(field => ({
        code: field.code,
        value: serializeAnswerValue(field, values.answers?.[field.code])
    }));
    const selectedRoom = values.reserveAccommodation
        ? findRoomAvailability(form.roomAvailability, values.accommodationSelectionKey)
        : undefined;
    const accommodationRoomCount = Number(values.accommodationRoomCount ?? 1);
    const accommodationNightCount = calculateNightCount(
        values.accommodationArrivalDate,
        values.accommodationDepartureDate);
    const items: PublicRegistrationSubmitPayload["items"] = [];

    const selectedParticipationFee = form.participationFees
        ?.find(fee => fee.feePriceId === Number(values.selectedParticipationFeePriceId));
    const requiresParticipantTypeProof = selectedParticipationFee?.requiresParticipantTypeProof === true;
    const participantTypeProofFileId = Number(values.participantTypeProofFileId);
    if (form.hasParticipationFee !== false) {
        if (!selectedParticipationFee) {
            throw new Error("Participation fee is not configured.");
        }

        if (requiresParticipantTypeProof && (!Number.isInteger(participantTypeProofFileId) || participantTypeProofFileId <= 0)) {
            throw new Error("Participant type proof file is required.");
        }

        items.push({
            description: trimValue(selectedParticipationFee.labelHr || selectedParticipationFee.labelEn || "Kotizacija"),
            quantity: 1,
            unitPrice: Number(selectedParticipationFee.amount ?? 0),
            isAccommodation: false,
            itemType: EventOfferLineItemTypeValue.Participation,
            sourceType: EventOfferLineSourceTypeValue.EventFeePrice,
            sourceId: selectedParticipationFee.feePriceId
        });
    }

    const activityAnswers = buildPublicRegistrationActivityAnswers(values, form);
    const answeredOptionIds = new Set(activityAnswers.map(answer => answer.eventOptionId));
    (form.registrationOptions ?? [])
        .filter(option => answeredOptionIds.has(option.id) && Number(option.price ?? 0) >= 0)
        .forEach(option => {
            items.push({
                description: trimValue(option.labelHr || option.labelEn || "Opcija"),
                quantity: 1,
                unitPrice: Number(option.price ?? 0),
                isAccommodation: false,
                itemType: EventOfferLineItemTypeValue.Option,
                sourceType: EventOfferLineSourceTypeValue.EventOption,
                sourceId: option.id
            });
        });

    if (selectedRoom) {
        items.push({
            description: `${selectedRoom.hotelName} - ${selectedRoom.roomTypeName}`,
            quantity: accommodationNightCount * accommodationRoomCount,
            unitPrice: Number(selectedRoom.pricePerNight ?? 0),
            isAccommodation: true,
            itemType: EventOfferLineItemTypeValue.Accommodation,
            sourceType: EventOfferLineSourceTypeValue.EventRoomReservation
        });
    }

    if (items.length === 0 && form.hasParticipationFee !== false) {
        throw new Error("At least one registration item is required.");
    }

    const hasParticipationItems = items.some(item => !item.isAccommodation && isPayableRegistrationItem(item));
    const hasAccommodationItems = items.some(item => item.isAccommodation && isPayableRegistrationItem(item));

    if (hasParticipationItems && getPaymentMethodsForCategory(form.paymentMethods, PaymentMethodCategoryValue.Participation).length === 0) {
        throw new Error("No payment methods are configured for participation.");
    }

    if (hasAccommodationItems && getPaymentMethodsForCategory(form.paymentMethods, PaymentMethodCategoryValue.Accommodation).length === 0) {
        throw new Error("No payment methods are configured for accommodation.");
    }

    const normalizedPayerType = values.payerType ?? PayerTypeValue.Individual;
    const shouldSendCompanyStylePayerFields = requiresCompanyStylePayerFields(normalizedPayerType);

    return {
        firstName: trimValue(values.firstName),
        lastName: trimValue(values.lastName),
        email: trimValue(values.email),
        phoneNumber: trimValue(values.phoneNumber),
        company: trimValue(values.company),
        linkedInUrl: trimValue(values.linkedInUrl),
        additionalInfo: "",
        payerType: normalizedPayerType,
        payerName: shouldSendCompanyStylePayerFields ? trimValue(values.payerName) : "",
        payerEmail: shouldSendCompanyStylePayerFields ? trimValue(values.payerEmail) : "",
        payerPhone: shouldSendCompanyStylePayerFields ? trimValue(values.payerPhone) : "",
        payerAddress: trimValue(values.payerAddress),
        payerCity: trimValue(values.payerCity),
        payerCountry: trimValue(values.payerCountry),
        payerTaxId: trimValue(values.payerTaxId),
        payerContactPerson: shouldSendCompanyStylePayerFields ? trimValue(values.payerContactPerson) : "",
        accommodationHotelId: selectedRoom?.hotelId ?? null,
        accommodationRoomTypeId: selectedRoom?.roomTypeId ?? null,
        accommodationArrivalDate: selectedRoom ? formatDateOnly(values.accommodationArrivalDate) : null,
        accommodationDepartureDate: selectedRoom ? formatDateOnly(values.accommodationDepartureDate) : null,
        accommodationRoomCount: selectedRoom ? accommodationRoomCount : null,
        participantTypeProofFileId: requiresParticipantTypeProof ? participantTypeProofFileId : null,
        participationPaymentMethod: values.participationPaymentMethod ?? PaymentMethodValue.BankTransfer,
        accommodationPaymentMethod: values.accommodationPaymentMethod ?? PaymentMethodValue.BankTransfer,
        gdprConsentAccepted: values.gdprConsentAccepted === true,
        items,
        answers,
        activityAnswers
    };
};

export const buildPublicRegistrationBulkSubmitPayloads = (
    values: PublicRegistrationFormValues,
    form: PublicEventFormResponse
): PublicRegistrationSubmitPayload[] => {
    const selectedParticipationFee = form.participationFees
        ?.find(fee => fee.feePriceId === Number(values.selectedParticipationFeePriceId));
    if (selectedParticipationFee?.requiresParticipantTypeProof === true) {
        throw new Error("Participant type proof file requires individual registration.");
    }

    return (values.bulkRows ?? []).map(row => buildPublicRegistrationSubmitPayload({
        ...values,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        phoneNumber: row.phoneNumber,
        company: row.company,
        linkedInUrl: row.linkedInUrl,
        answers: row.answers ?? {},
        selectedOptionIds: row.selectedOptionIds ?? [],
        activityAnswers: row.activityAnswers ?? {},
        reserveAccommodation: false,
        accommodationSelectionKey: undefined,
        accommodationArrivalDate: undefined,
        accommodationDepartureDate: undefined,
        accommodationRoomCount: undefined
    }, form));
};
